import { format, parse } from "date-fns";

export const formatDate = (
  date: Date | string | undefined | null,
  withTime = false,
  exact = false,
) => {
  if (!date) {
    return "-";
  }

  const options: Intl.DateTimeFormatOptions = !withTime
    ? {
        year: "numeric",
        month: "numeric",
        day: "numeric",
      }
    : !exact
      ? {
          year: "numeric",
          month: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
        }
      : {
          year: "numeric",
          month: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
          second: "numeric",
        };

  // Date-only API values (DateOnly → "yyyy-MM-dd") have no time/zone and are parsed
  // as UTC midnight. Formatting them in the local zone would shift the day backwards
  // for users west of UTC, so render them in UTC to keep the calendar day stable.
  // Full timestamps (with a "Z"/offset) still convert to the user's local time.
  const isDateOnly =
    typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date);
  if (isDateOnly) {
    options.timeZone = "UTC";
  }

  const format = new Intl.DateTimeFormat("cs", options);

  return format.format(new Date(date));
};

export const formatMonthAndYear = (
  year: number,
  month: number,
  locale: string,
) =>
  new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
  }).format(new Date(year, month - 1, 1));

export const parseStringTimeToDate = (hms: string) =>
  parse(hms, "HH:mm:ss", new Date()); // parse "23:45:12" into a Date today at 23:45:12

export const formatDateTimeToTimeString = (date: Date) =>
  format(date, "HH:mm:ss"); // format Date to "23:45:12"

export const formatDateToDateString = (date: Date) =>
  format(date, "yyyy-MM-dd"); // 2025-03-10

export const mergeDateTimeToUTC = (date: string, time: string): string => {
  const localDateTime = `${date}T${time}`;
  const utcDateTime = new Date(localDateTime).toISOString();
  return utcDateTime;
};

export const getDaysUntilDate = (date: Date | string | null): number => {
  if (!date) {
    return 0;
  }

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const targetDate = new Date(date);
  targetDate.setHours(0, 0, 0, 0);

  return Math.ceil(
    (targetDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
  );
};

export const formatTransactionDateTime = (
  date: Date | string | undefined | null,
): string => {
  if (!date) {
    return "-";
  }

  const dateObject = new Date(date);
  const timeString = format(dateObject, "HH:mm");
  const dateString = format(dateObject, "d.M.yyyy");

  return `${timeString} | ${dateString}`;
};

// The backend buckets daily periods (loyalty events, streaks) by Czech calendar day — use this
// wherever the FE must agree with it; the device's local date drifts outside Europe/Prague.
const czechDateFormat = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Prague",
});

/** Calendar day of `date` in Europe/Prague, as "yyyy-MM-dd". */
export const getCzechDateString = (date: Date = new Date()): string =>
  czechDateFormat.format(date);
